import { TUNING_SETTING_DESCRIPTORS, TUNING_STRATEGY_IDS } from '@playcastdotio/PeerCore';
import type { TuningCapabilities, TuningCommand, TuningIdentity, TuningPatch, TuningReceipt,
  TuningSettingDescriptor, TuningSettingKey, TuningSettingReceipt, WebRtcOptimizationManager } from '@playcastdotio/PeerCore';
import type { IHostTuningRuntime } from '../../../streamTuning/HostTuningContracts';
import type { IRuntimeRecoveryPolicy } from './RuntimeRecoveryPolicy';

export type HostTuningRuntimeDependencies = {
  optimization: WebRtcOptimizationManager; recovery: IRuntimeRecoveryPolicy;
  connection(peerId: string): RTCPeerConnection | null; eligible(): boolean;
  buildId: string; runtimeId: string;
  configureTelemetry?(target: TuningIdentity, patch: TuningPatch): Promise<readonly TuningSettingReceipt[]>;
  restoreTelemetry?(target: TuningIdentity): void;
};
const senderKeys: readonly TuningSettingKey[] = ['video.maxBitrateBps', 'video.maxFramerate',
  'video.resolutionTier', 'video.degradationPreference', 'audio.maxBitrateBps'];

/** Native acceptance/readback are receipts, never proof of actual encoded cadence. */
export class HostTuningRuntimeAdapter implements IHostTuningRuntime {
  constructor(private readonly deps: HostTuningRuntimeDependencies) {}
  public capabilities(target: TuningIdentity): TuningCapabilities {
    const pc = this.deps.connection(target.peerId);
    const hasVideo = !!pc?.getSenders().some(sender => sender.track?.kind === 'video');
    const settings: TuningCapabilities['settings'] = {};
    for (const [key, descriptor] of Object.entries(TUNING_SETTING_DESCRIPTORS) as [TuningSettingKey, TuningSettingDescriptor][]) {
      const { boolean: supportsBoolean, nullable: _nullable, ...support } = descriptor;
      const supported = (hasVideo && senderKeys.includes(key)) || key.startsWith('recovery.') ||
        (!!this.deps.configureTelemetry && ['telemetry.windowMs', 'telemetry.statsIntervalMs'].includes(key));
      const fresh = ['video.codec', 'video.temporalLayers', 'video.nackRtx', 'ice.transportPolicy', 'audio.stereo', 'audio.packetTimeMs'].includes(key);
      settings[key] = { ...support, ...(supportsBoolean ? { values: [true, false] } : {}),
        mode: supported ? 'live' : fresh ? 'fresh-stream' : 'unsupported',
        nativeApi: supported ? 'supported' : 'unknown', ...(supported || fresh ? {} : { reason: 'Runtime actuator unavailable' }) };
    }
    return { protocolVersion: 1, eligible: this.deps.eligible(), armed: false, endpoint: 'host',
      buildId: this.deps.buildId, runtimeId: this.deps.runtimeId, settings, strategies: TUNING_STRATEGY_IDS };
  }
  public async apply(command: TuningCommand): Promise<TuningReceipt> {
    const settings: TuningSettingReceipt[] = [];
    if (command.action.kind !== 'apply-live') return this.receipt(command, 'rejected', [], 'fresh-trial-required');
    if (!this.deps.eligible()) return this.receipt(command, 'rejected', [], 'ineligible-host');
    const pc = this.deps.connection(command.target.peerId);
    if (!pc) return this.receipt(command, 'failed', [], 'connection-unavailable');
    const patch = command.action.patch;
    const senderPatch = Object.fromEntries(Object.entries(patch).filter(([key]) => senderKeys.includes(key as TuningSettingKey)));
    try {
      this.deps.optimization.configureTuning(pc, command.target);
      if (Object.keys(senderPatch).length) settings.push(...(await this.deps.optimization.applyTuningParameters(pc, command.target, senderPatch)).settings);
      settings.push(...await this.deps.recovery.apply(command.target, patch));
      if (this.deps.configureTelemetry) settings.push(...await this.deps.configureTelemetry(command.target, patch));
      for (const [key, requested] of Object.entries(patch)) {
        if (!settings.some(setting => setting.key === key)) settings.push({ key: key as TuningSettingKey, requested,
          status: this.capabilities(command.target).settings[key as TuningSettingKey]?.mode === 'fresh-stream'
            ? 'requires-fresh-stream' : 'unsupported', reason: 'No live actuator for this setting' });
      }
      return this.receipt(command, settings.some(setting => ['failed', 'unsupported', 'requires-fresh-stream'].includes(setting.status)) ? 'failed' : 'applied', settings);
    } catch (error) { return this.receipt(command, 'failed', settings, String((error as Error).message).slice(0, 128)); }
  }
  public async restore(target: TuningIdentity): Promise<TuningReceipt> {
    const pc = this.deps.connection(target.peerId);
    const command: TuningCommand = { version: 1, commandId: `restore-${target.trialId}`, target,
      expectedRevision: target.configRevision, leaseId: 'internal-production-restore', ttlMs: 10_000, action: { kind: 'stop' } };
    try {
      if (pc) { this.deps.optimization.configureTuning(pc, target); await this.deps.optimization.releaseTuning(pc, target); }
      await this.deps.recovery.restore(target);
      this.deps.restoreTelemetry?.(target);
      return this.receipt(command, 'applied', []);
    } catch (error) { return this.receipt(command, 'failed', [], (error as Error).message); }
  }
  public async dispose(target: TuningIdentity): Promise<void> {
    const pc = this.deps.connection(target.peerId);
    if (pc) this.deps.optimization.disposeTuningConnection(pc);
    await this.deps.recovery.restore(target);
    this.deps.restoreTelemetry?.(target);
  }
  private receipt(command: TuningCommand, stage: TuningReceipt['stage'], settings: readonly TuningSettingReceipt[], code?: string): TuningReceipt {
    return { version: 1, commandId: command.commandId, target: command.target, stage, settings,
      policyVersion: this.deps.recovery.snapshot(command.target.peerId).policyVersion,
      ...(code ? { error: { code, message: code } } : {}) };
  }
}
